/**
 * GenerationProgress Component
 *
 * Displays the progress of AI package generation with stage indicators.
 * Supports different job types with appropriate stage sets.
 */

import type { GenerationJobStatus, GenerationJobType, PackageSummary } from '../lib/types';

interface GenerationProgressProps {
  status: GenerationJobStatus | null;
  progressMessage: string | null;
  errorMessage: string | null;
  packageSummary: PackageSummary | null;
  startedAt: string | null;
  completedAt: string | null;
  /** Job type determines which stages to show */
  jobType?: GenerationJobType;
}

interface Stage {
  status: GenerationJobStatus;
  label: string;
  icon: string;
}

// Full job stages (all stages)
const FULL_STAGES: Stage[] = [
  { status: 'pending', label: 'Queued', icon: '⏳' },
  { status: 'analyzing', label: 'Analyzing', icon: '🔍' },
  { status: 'generating_competencies', label: 'Competencies', icon: '📋' },
  { status: 'generating_scenario', label: 'Scenario', icon: '🎬' },
  { status: 'generating_persona', label: 'Persona', icon: '👤' },
  { status: 'generating_rubric', label: 'Rubric', icon: '📊' },
  { status: 'completed', label: 'Complete', icon: '✓' },
];

// Competencies-only job stages (step 1)
const COMPETENCIES_STAGES: Stage[] = [
  { status: 'pending', label: 'Queued', icon: '⏳' },
  { status: 'analyzing', label: 'Analyzing', icon: '🔍' },
  { status: 'generating_competencies', label: 'Competencies', icon: '📋' },
  { status: 'completed', label: 'Complete', icon: '✓' },
];

// Content-only job stages (step 2)
const CONTENT_STAGES: Stage[] = [
  { status: 'pending', label: 'Queued', icon: '⏳' },
  { status: 'generating_scenario', label: 'Scenario', icon: '🎬' },
  { status: 'generating_persona', label: 'Persona', icon: '👤' },
  { status: 'generating_rubric', label: 'Rubric', icon: '📊' },
  { status: 'completed', label: 'Complete', icon: '✓' },
];

function getStagesForJobType(jobType?: GenerationJobType): Stage[] {
  if (jobType === 'competencies') return COMPETENCIES_STAGES;
  if (jobType === 'content') return CONTENT_STAGES;
  return FULL_STAGES;
}

const STAGE_ORDER: Record<GenerationJobStatus, number> = {
  pending: 0,
  analyzing: 1,
  generating_competencies: 2,
  generating_scenario: 3,
  generating_persona: 4,
  generating_rubric: 5,
  completed: 6,
  failed: -1,
};

function getStageState(
  stageStatus: GenerationJobStatus,
  currentStatus: GenerationJobStatus | null
): 'completed' | 'current' | 'pending' {
  if (!currentStatus || currentStatus === 'failed') return 'pending';

  const stageOrder = STAGE_ORDER[stageStatus];
  const currentOrder = STAGE_ORDER[currentStatus];

  if (stageOrder < currentOrder) return 'completed';
  if (stageOrder === currentOrder) return 'current';
  return 'pending';
}

function formatDuration(startedAt: string, completedAt: string | null): string {
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const durationSec = Math.round((end - start) / 1000);

  if (durationSec < 60) return `${durationSec}s`;
  const minutes = Math.floor(durationSec / 60);
  const seconds = durationSec % 60;
  return `${minutes}m ${seconds}s`;
}

export function GenerationProgress({
  status,
  progressMessage,
  errorMessage,
  packageSummary,
  startedAt,
  completedAt,
  jobType,
}: GenerationProgressProps) {
  const isFailed = status === 'failed';
  const isCompleted = status === 'completed';
  const stages = getStagesForJobType(jobType);

  // Determine header text based on job type
  const getHeaderText = () => {
    if (isFailed) return 'Generation Failed';
    if (isCompleted) {
      if (jobType === 'competencies') return 'Competencies Generated';
      if (jobType === 'content') return 'Content Generated';
      return 'Generation Complete';
    }
    if (jobType === 'competencies') return 'Generating Competencies...';
    if (jobType === 'content') return 'Generating Content...';
    return 'Generating Package...';
  };

  return (
    <div
      style={{
        background: 'var(--bg-muted)',
        borderRadius: 8,
        padding: 20,
        marginTop: 16,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
          {getHeaderText()}
        </h4>
        {startedAt && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {formatDuration(startedAt, completedAt)}
          </span>
        )}
      </div>

      {/* Progress stages */}
      {!isFailed && (
        <div
          style={{
            display: 'flex',
            gap: 4,
            marginBottom: 16,
          }}
        >
          {stages.map((stage) => {
            const state = getStageState(stage.status, status);
            return (
              <div
                key={stage.status}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {/* Progress bar segment */}
                <div
                  style={{
                    width: '100%',
                    height: 4,
                    borderRadius: 2,
                    background:
                      state === 'completed'
                        ? 'var(--semantic-success)'
                        : state === 'current'
                          ? 'var(--semantic-info)'
                          : 'var(--border-default)',
                    transition: 'background 0.3s ease',
                  }}
                />
                {/* Icon */}
                <span
                  style={{
                    fontSize: 16,
                    opacity: state === 'pending' ? 0.4 : 1,
                    filter: state === 'current' ? 'none' : 'grayscale(0.3)',
                  }}
                  title={stage.label}
                >
                  {stage.icon}
                </span>
                {/* Label */}
                <span
                  style={{
                    fontSize: 10,
                    color: state === 'current' ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontWeight: state === 'current' ? 600 : 400,
                    textAlign: 'center',
                  }}
                >
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Progress message */}
      {progressMessage && !isFailed && !isCompleted && (
        <div
          style={{
            fontSize: 13,
            color: 'var(--text-secondary)',
            fontStyle: 'italic',
            marginBottom: 12,
          }}
        >
          {progressMessage}
        </div>
      )}

      {/* Error display */}
      {isFailed && errorMessage && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--semantic-error-light)',
            borderRadius: 4,
            color: 'var(--semantic-error-text)',
            fontSize: 13,
          }}
        >
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      {/* Package summary */}
      {isCompleted && packageSummary && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--semantic-success-light)',
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--semantic-success-text)' }}>
            Package Generated
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>Competencies:</span>{' '}
              <strong>{packageSummary.competencyCount}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>Scenario:</span>{' '}
              <strong>{packageSummary.scenarioTitle}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>Persona:</span>{' '}
              <strong>{packageSummary.personaName}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>Generated:</span>{' '}
              <span className="mono" style={{ fontSize: 11 }}>
                {new Date(packageSummary.generatedAt).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
